import {useState, useEffect, useRef} from 'react';
import apiConfig from '~Configs/apiConfig';
import {useTranslation} from 'react-i18next';
import {trackEvent} from '@src/Tracking/trackEvent';
import {TraceTokEvents} from '@src/Tracking/catalog';
import getFirebaseTokenId from '@src/Api/TraceApi/utils/getFirebaseToken';
import {SESSION_ID_KEY} from '~Providers/SessionProvider/useSessionId';
interface IDownloadCertificateArg {
  id: string;
  culture: string;
  isCourse: boolean;
  filename?: string;
}

const useDownloadCertificate = (): [
  fetch: ({
    id,
    culture,
    isCourse,
  }: IDownloadCertificateArg) => Promise<string | undefined>,
  {
    isLoading: boolean;
    resetProgress: () => void;
  },
  progress?: number,
] => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<number>();
  const connectionTimerRef = useRef<number | null>(null);
  const {t} = useTranslation('profile');

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (connectionTimerRef.current) {
        clearInterval(connectionTimerRef.current);
      }
    };
  }, []);

  const downloadPdf = async (url: string, filename: string, postData: any) => {
    try {
      const token = await getFirebaseTokenId();

      // Set initial progress to indicate connection is starting
      setProgress(0.05);

      // Create a simulation of backend processing time
      // Will increase progress from 5% to 20% over a few seconds
      // to give visual feedback during backend processing
      let connectionProgress = 0.05;
      connectionTimerRef.current = window.setInterval(() => {
        // Slowly increase progress during initial connection phase
        if (connectionProgress < 0.2) {
          connectionProgress += 0.01;
          setProgress(connectionProgress);
        } else {
          // Stop the timer once we reach 20%
          if (connectionTimerRef.current) {
            clearInterval(connectionTimerRef.current);
            connectionTimerRef.current = null;
          }
        }
      }, 300);

      // Create a fetch request with progress tracking
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.responseType = 'blob';
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      // set session id
      const sessionId = localStorage.getItem(SESSION_ID_KEY);
      xhr.setRequestHeader('x-session-id', sessionId || '');

      // Track download progress once data starts flowing
      xhr.onprogress = event => {
        // Stop the connection timer if it's still running
        if (connectionTimerRef.current) {
          clearInterval(connectionTimerRef.current);
          connectionTimerRef.current = null;
        }

        if (event.lengthComputable) {
          // Scale actual download progress from 20% to 100%
          const downloadProgress = 0.2 + (event.loaded / event.total) * 0.8;
          setProgress(downloadProgress);
        }
      };

      return new Promise<string>((resolve, reject) => {
        xhr.onload = function () {
          // Clear any remaining timers
          if (connectionTimerRef.current) {
            clearInterval(connectionTimerRef.current);
            connectionTimerRef.current = null;
          }

          if (this.status === 200 || this.status === 201) {
            setProgress(1);

            // Create a blob and URL for downloading
            const blob = new Blob([this.response], {type: 'application/pdf'});
            const blobUrl = window.URL.createObjectURL(blob);

            // Create and trigger download
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = blobUrl;
            a.download = `${filename}.pdf`;
            document.body.appendChild(a);
            a.click();

            // Clean up
            window.URL.revokeObjectURL(blobUrl);
            document.body.removeChild(a);

            resolve(blobUrl);
          } else {
            setProgress(undefined);
            trackEvent(TraceTokEvents.ErrorEncountered, {
              error_surface: 'certificate',
              error_code_bucket: 'download',
            });
            reject(new Error('Download failed'));
          }
        };

        xhr.onerror = function () {
          // Clear any remaining timers
          if (connectionTimerRef.current) {
            clearInterval(connectionTimerRef.current);
            connectionTimerRef.current = null;
          }

          setProgress(undefined);
          trackEvent(TraceTokEvents.ErrorEncountered, {
            error_surface: 'certificate',
            error_code_bucket: 'network',
          });
          reject(new Error('Network error'));
        };

        // Send the request with the data
        xhr.send(JSON.stringify(postData));
      });
    } catch (error) {
      // Clear any remaining timers
      if (connectionTimerRef.current) {
        clearInterval(connectionTimerRef.current);
        connectionTimerRef.current = null;
      }

      setProgress(undefined);
      trackEvent(TraceTokEvents.ErrorEncountered, {
        error_surface: 'certificate',
        error_code_bucket: 'unknown',
      });
      console.error('Download error:', error);
      throw error;
    }
  };

  const downloadFile = async ({
    id,
    culture,
    isCourse,
    filename,
  }: IDownloadCertificateArg): Promise<string | undefined> => {
    const url = isCourse
      ? 'me/generate-certificate/course'
      : 'me/generate-certificate/obtained-job';
    const body = isCourse
      ? {
          courseId: id,
          culture,
        }
      : {
          certificateId: id,
          culture,
        };
    try {
      setLoading(true);
      // Initial progress is now set in downloadPdf

      const date = new Date();
      const defaultFilename =
        'certificate_' +
        date.toJSON().slice(0, 10) +
        '_' +
        date.getHours() +
        '_' +
        date.getMinutes() +
        '_' +
        date.getSeconds();

      const res = await downloadPdf(
        apiConfig.traceApiUrl + url,
        filename || defaultFilename,
        body,
      );

      return res;
    } catch (error) {
      console.error('Download file error:', error);
      return undefined;
    } finally {
      setLoading(false);
    }
  };

  const resetProgress = () => {
    if (connectionTimerRef.current) {
      clearInterval(connectionTimerRef.current);
      connectionTimerRef.current = null;
    }
    setProgress(undefined);
  };

  return [downloadFile, {isLoading: loading, resetProgress}, progress];
};

export default useDownloadCertificate;
